'use client';

/**
 * DemoExtractedLinesCard — operational post-run lens for ingest cases.
 *
 * After `schedule_ingest` ran, the world has new `:LineItem` + BW_ nodes
 * under the resulting `:Intent`. This card reads those *entities* via
 * `intent_lines(intent_sid)` — not the audit conversation. Doctrine: the
 * audit captures *that* the work happened; the entities are *what* the
 * work produced. Post-run state is the right source.
 *
 * Self-suppresses when no scene's captures carry an `intent_sid` — so
 * search-shape cases (no ingest) don't paint it. The substrate handle
 * is `captures.intent_sid` surfaced on the StepSlice by `test_run_view`;
 * the card finds it and fires the operational read.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useQuery } from '@tanstack/react-query';
import { ListChecks, FileText } from 'lucide-react';
import { api } from '@/lib/api/client';
import { useDemoAskStore } from '@/lib/stores/demo-ask-store';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import type { TestRunView } from './demo-test-run-types';


/** Mirrors klyde_edge/discovery/intent_responses.py::IngestedWish. */
interface IngestedWish {
  line_sid: string;
  wish_sid: string;
  name: string;
  state: string;
  quantity: number | null;
  target_price: number | null;
  confidence: number;
  domain: string | null;
  source_page: number | null;
  vendor_hint: string | null;
  moq: number | null;
  lead_time_estimate: number | null;
  delivery_location: string | null;
  description: string | null;
  material_hint: string | null;
  raw_text: string | null;
  enrichment_status: string | null;
}


interface WishListResponse {
  intent_sid: string;
  lines: IngestedWish[];
  total: number;
}


export function DemoExtractedLinesCard() {
  const T = useCardTypography();
  const simulationId = useDemoAskStore((s) => s.testRun?.simulation_id ?? null);

  const { data: runView } = useQuery<TestRunView>({
    queryKey: ['test_run_view', simulationId],
    queryFn: async () => {
      const res = await api.getStrategyData<TestRunView>('test_run_view', {
        simulation_id: simulationId,
      });
      if (!res.success) throw new Error(res.error || 'test_run_view failed');
      return res.data;
    },
    enabled: Boolean(simulationId),
    staleTime: 30_000,
  });

  // Find an intent_sid handle in any scene's captures. Substrate handle
  // pattern — the case's `outputs` decoration named it at runtime; we
  // read it without knowing which scene wrote it.
  const intentSid = runView?.scenes
    .map((s) => (s.captures && typeof s.captures.intent_sid === 'string' ? s.captures.intent_sid : null))
    .find((v) => v && v.startsWith('IN_'));

  const { data: wishList } = useQuery<WishListResponse>({
    queryKey: ['intent_lines', intentSid],
    queryFn: async () => {
      const res = await api.getStrategyData<WishListResponse>('intent_lines', {
        intent_sid: intentSid,
      });
      if (!res.success) throw new Error(res.error || 'intent_lines failed');
      return res.data;
    },
    enabled: Boolean(intentSid),
    staleTime: 15_000,
  });

  if (!simulationId || !intentSid) return null;
  if (!wishList || wishList.lines.length === 0) return null;

  return (
    <div className="bg-card/30 border border-border rounded-2xl px-5 py-4 mb-4">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <ListChecks className="w-4 h-4 text-muted-foreground" />
          <div className={cn(T.body, 'text-sm font-medium')}>Extracted lines</div>
        </div>
        <div className={cn(T.metadata, 'text-muted-foreground text-xs font-mono')}>
          {wishList.intent_sid} · {wishList.total} line{wishList.total === 1 ? '' : 's'}
        </div>
      </div>
      <p className={cn(T.metadata, 'text-muted-foreground text-xs mb-3')}>
        Read from the operational substrate (LineItems + BW_ products), not the audit conversation.
      </p>

      <div className="space-y-1.5">
        {wishList.lines.map((line) => (
          <LineRow key={line.line_sid} line={line} />
        ))}
      </div>
    </div>
  );
}


function LineRow({ line }: { line: IngestedWish }) {
  const T = useCardTypography();
  return (
    <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-3 items-baseline px-3 py-2 rounded-lg bg-muted/30">
      <div className="min-w-0">
        <div className={cn(T.body, 'text-xs font-medium truncate')} title={line.name}>
          {line.name}
        </div>
        <div className={cn(T.metadata, 'text-muted-foreground text-[10px] font-mono mt-0.5')}>
          {line.line_sid} · wish {line.wish_sid}
        </div>
      </div>
      {line.vendor_hint && (
        <div className={cn(T.metadata, 'text-muted-foreground text-[11px]')}>
          {line.vendor_hint}
        </div>
      )}
      {typeof line.quantity === 'number' && (
        <div className={cn(T.body, 'text-xs tabular-nums')}>
          ×{line.quantity}
        </div>
      )}
      {typeof line.target_price === 'number' && (
        <div className={cn(T.body, 'text-xs tabular-nums font-medium')}>
          ${line.target_price.toLocaleString()}
        </div>
      )}
      <StateBadge state={line.state} enrichment={line.enrichment_status} />
    </div>
  );
}


function StateBadge({ state, enrichment }: { state: string; enrichment: string | null }) {
  // enrichment_status takes precedence when in-flight — the demo audience
  // cares whether the line is fully enriched, not just whether it's draft.
  const isInFlight = enrichment === 'pending' || enrichment === 'enriching';
  const isEnriched = enrichment === 'enriched';
  const label = isInFlight ? enrichment : (isEnriched ? 'enriched' : state);
  const styles = isInFlight
    ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
    : isEnriched
      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
      : 'bg-muted-foreground/15 text-muted-foreground';
  return (
    <span className={cn('text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded', styles)}>
      {label}
    </span>
  );
}
